import { calculateExpDate, getStoreState, PUT_POLICY, save, setStoreState } from '@cogitate/ui-multi-utils';
import { commonKeys, storeKeys } from 'ui/data/constants/keys';
import {
  buildCoveredPractitionerElement,
  getAdditionalLocations,
  setAdditionalLocations,
  setOfficeContacts,
} from '../helper';
import { toast } from 'react-toastify';
import NProgress from 'nprogress';
import { payrollProtectionKeys, routeKeys } from 'ui/data/commercial/payrollprotection/constants/keys';
import { alertMsgs } from 'ui/data/constants/messages';

import { applyResolvedPayrollProtectionAgesToPolicy } from 'ui/utilities/commercial/payrollprotection/practitionerAge';

const isPayrollProtectionAgeQualificationError = (message) =>
  message === alertMsgs.ModalMsgs.AgeGreaterThan65 ||
  message ===
    'This practitioner is not eligible for this product, please contact your Vital underwriter for more details.' ||
  (typeof message === 'string' && message.includes('not eligible for this product'));

const handleNameChange = (model, _changedKey) => {
  const firstName = model?.InsuredAccount?.FirstName || '';
  const middleName = model?.InsuredAccount?.MiddleName || '';
  const lastName = model?.InsuredAccount?.LastName || '';
  const suffix = model?.InsuredAccount?.Suffix || '';

  // Create display name by combining first, middle, and last names
  let displayName = '';
  if (firstName) {
    displayName += firstName;
  }
  if (middleName) {
    displayName += (displayName ? ' ' : '') + middleName;
  }
  if (lastName) {
    displayName += (displayName ? ' ' : '') + lastName;
  }
  if (suffix) {
    displayName += (displayName ? ' ' : '') + suffix;
  }
  // Update the display name in the model
  if (model.InsuredAccount) {
    model.InsuredAccount.DisplayName = displayName;
  }
};

export const onApplicationPolicyHolderSchemaChange =
  ({
    payload: {
      model: changedModel,
      key: key,
      schema: [_schema],
    },
  }) =>
  (_dispatch, _getState) => {
    switch (key) {
      case payrollProtectionKeys.FIRST_NAME:
      case payrollProtectionKeys.MIDDLE_NAME:
      case payrollProtectionKeys.LAST_NAME:
      case payrollProtectionKeys.SUFFIX:
        handleNameChange(changedModel, key);
        break;

      case payrollProtectionKeys.NO_OF_PRACTITIONERS:
        changedModel = buildCoveredPractitionerElement(changedModel);
        break;

      // Address fields for practice location
      case payrollProtectionKeys.ADDRESS_LINE1:
      case payrollProtectionKeys.ADDRESS_LINE2:
      case payrollProtectionKeys.CITY:
      case payrollProtectionKeys.STATE:
      case payrollProtectionKeys.ZIP:
      case payrollProtectionKeys.COUNTRY:
      case payrollProtectionKeys.LOCATIONS:
        changedModel.Risks.PayrollProtectionUnit[0].PracticeLocationAddresses.IsManual = true;
        break;

      case payrollProtectionKeys.IS_MANUAL_ADDRESS:
        changedModel.Risks.PayrollProtectionUnit[0].PracticeLocationAddresses.IsManual =
          !!changedModel.Risks.PayrollProtectionUnit[0].PracticeLocationAddresses.IsManual;
        changedModel.Risks.PayrollProtectionUnit[0].PracticeLocationAddresses.AddressLine1 = '';
        changedModel.Risks.PayrollProtectionUnit[0].PracticeLocationAddresses.AddressLine2 = '';
        changedModel.Risks.PayrollProtectionUnit[0].PracticeLocationAddresses.City = '';
        changedModel.Risks.PayrollProtectionUnit[0].PracticeLocationAddresses.State = '';
        changedModel.Risks.PayrollProtectionUnit[0].PracticeLocationAddresses.Zip = '';
        changedModel.Risks.PayrollProtectionUnit[0].PracticeLocationAddresses.Country = '';
        break;

      case payrollProtectionKeys.AddOfficeContacts:
        changedModel.Risks.PayrollProtectionUnit[0]?.PractitionerRiskAttributes?.OfficeContacts.forEach(
          (OfficeContact) => {
            OfficeContact.Communications = [
              {
                Type: 'PhNo',
                SubType: 'Primary',
                Value:
                  OfficeContact?.Communications?.[0]?.Value.length > 0 ? OfficeContact?.Communications?.[0]?.Value : '',
              },
              {
                Type: 'Email',
                SubType: 'Primary',
                Value:
                  OfficeContact?.Communications?.[1]?.Value.length > 0 ? OfficeContact?.Communications?.[1]?.Value : '',
              },
            ];
          }
        );
        break;

      case payrollProtectionKeys.PolicyEffectiveDate:
        if (changedModel.EffectiveDate) {
          const newExpDate = calculateExpDate(changedModel.EffectiveDate, changedModel.PolicyTerm.Value);
          changedModel.ExpirationDate = newExpDate;
          changedModel.Transaction.EffectiveDate = changedModel.EffectiveDate;
        } else {
          changedModel.ExpirationDate = '';
        }
        break;

      case payrollProtectionKeys.DATE_OF_BIRTH:
      case payrollProtectionKeys.PractitionerDateOfBirth:
        applyResolvedPayrollProtectionAgesToPolicy(changedModel);
        break;

      case payrollProtectionKeys.AddPractitionerLocations:
        if (
          changedModel.Risks.PayrollProtectionUnit[0]?.PractitionerRiskAttributes?.PracticeLocationAddresses.length > 5
        ) {
          changedModel.Risks.PayrollProtectionUnit[0].PractitionerRiskAttributes.PracticeLocationAddresses =
            changedModel.Risks.PayrollProtectionUnit[0]?.PractitionerRiskAttributes?.PracticeLocationAddresses.slice(
              0,
              5
            );
          toast.error('You can add a maximum of 5 addresses.');
        } else {
          changedModel.Risks.PayrollProtectionUnit[0].PractitionerRiskAttributes.PracticeLocationAddresses =
            changedModel.Risks.PayrollProtectionUnit[0]?.PractitionerRiskAttributes?.PracticeLocationAddresses.map(
              (address) => {
                const addressData = address.Address;
                const addressObj = {
                  Address: addressData,
                  NickName: '',
                  LocationNumber: 0,
                  IsValid: false,
                  IsPrimaryPracticeLocation: false,
                  IsManual: addressData?.IsManual || false,
                  StreetName: '',
                  AddressLine1: addressData?.AddressLine1 || '',
                  AddressLine2: addressData?.AddressLine1 || '',
                  City: addressData?.City || '',
                  State: addressData?.State || '',
                  County: addressData?.County || '',
                  Zip: addressData?.Postalcode || addressData?.Zip || '',
                  Country: addressData?.Country || 'United States',
                  CountyCode: '',
                  CountryCode: '',
                  PlaceId: addressData?.PlaceId || '',
                  Number: 0,
                  Name: '',
                  Long: addressData?.Long || '',
                  Lat: addressData?.Lat || '',
                  Description: '',
                  AddressType: 'p',
                  FormattedAddress: addressData?.FormattedAddress || '',
                  UnFormattedAddress: addressData?.UnFormattedAddress || '',
                  Status: '',
                  AptSuite: '',
                  PoBox: '',
                  CityCode: '',
                  Territory: '',
                  TerritoryCode: '',
                };
                return addressObj;
              }
            );
        }
        break;

      default:
        // Default behavior - just update the store
        // eslint-disable-next-line no-case-declarations
        const practiceLocationAddress = changedModel.Risks?.PayrollProtectionUnit[0]?.PracticeLocationAddresses || {};
        if (practiceLocationAddress?.Postalcode) {
          practiceLocationAddress.Zip = practiceLocationAddress.Postalcode;
          delete practiceLocationAddress.Postalcode;
        }

        //Underwriter Address Number
        if (changedModel.UnderWriter.Address.Number?.length > 0) {
          changedModel.UnderWriter.Address.Number = Number(changedModel.UnderWriter.Address.Number);
        } else {
          changedModel.UnderWriter.Address.Number = 0;
        }
        break;
    }

    if (key) {
      setStoreState(storeKeys.PolicyReducer, changedModel);
    }
  };

export const onApplicationPolicyHolderContactAdd =
  ({ payload: { model, state, setState, policyData } }) =>
  (_dispatch, _getState) => {
    const { officeContacts, editIndex } = state;

    let updatedContacts;
    if (editIndex !== null) {
      // Update existing contact
      const updatedContact = {
        ...officeContacts[editIndex],
        ContactType: model.ContactType,
        ContactName: model.ContactName,
        Communications: [
          {
            Type: 'PhNo',
            SubType: 'Primary',
            Value: model.ContactNumber || '',
          },
          {
            Type: 'Email',
            SubType: 'Primary',
            Value: model.ContactEmail || '',
          },
        ],
      };
      updatedContacts = officeContacts.map((c, idx) => (idx === editIndex ? updatedContact : c));
    } else {
      if (policyData?.Risks?.PayrollProtectionUnit?.[0]?.PractitionerRiskAttributes?.OfficeContacts.length > 2) {
        toast.error('You can add a maximum of 3 contacts.');
        return;
      }
      // Add new contact
      const newContact = {
        id: Date.now() + Math.random(),
        ContactType: model.ContactType,
        ContactName: model.ContactName,
        Communications: [
          {
            Type: 'PhNo',
            SubType: 'Primary',
            Value: model.ContactNumber || '',
          },
          {
            Type: 'Email',
            SubType: 'Primary',
            Value: model.ContactEmail || '',
          },
        ],
      };
      updatedContacts = [...officeContacts, newContact];
    }

    setState({
      officeContacts: updatedContacts,
      editIndex: null,
      contactFormData: {
        ContactType: '',
        ContactName: '',
        ContactNumber: '',
        ContactEmail: '',
      },
    });

    // Update Redux store
    if (policyData) {
      policyData.Risks.PayrollProtectionUnit[0].PractitionerRiskAttributes.OfficeContacts = updatedContacts;
      setOfficeContacts(updatedContacts, policyData);
    }
  };

export const onApplicationPolicyHolderContactEdit =
  ({ payload: { state, setState } }) =>
  (_dispatch, _getState) => {
    const { row, rowIndex } = state;
    setState({
      editIndex: rowIndex,
      contactFormData: {
        ContactType: row.ContactType,
        ContactName: row.ContactName,
        ContactNumber: row.Communications?.[0]?.Value || '',
        ContactEmail: row.Communications?.[1]?.Value || '',
      },
    });
  };

export const onApplicationPolicyHolderContactDelete = ({ state, setState, policyData }) => {
  const { officeContacts, row } = state;
  const updatedContacts = officeContacts.filter((contact) => contact.id !== row.id);
  setState({
    officeContacts: updatedContacts,
    editIndex: null,
    contactFormData: {
      ContactType: '',
      ContactName: '',
      ContactNumber: '',
      ContactEmail: '',
    },
  });
  if (policyData) {
    policyData.Risks.PayrollProtectionUnit[0].PractitionerRiskAttributes.OfficeContacts = updatedContacts;
    setOfficeContacts(updatedContacts, policyData);
  }
};

export const onApplicationPolicyHolderAdditionalLocationAdd =
  ({ payload: { model, state, setState, policyData } }) =>
  (_dispatch, _getState) => {
    const { editLocationIndex } = state;

    if (editLocationIndex !== null) {
      // Update existing location

      const currentLocations = getAdditionalLocations(policyData);
      let UnFormattedAddress = '';
      if (model.IsManual) {
        if (model.AddressLine2 !== '') {
          UnFormattedAddress =
            model.AddressLine1 +
            ' ' +
            model.AddressLine2 +
            ', ' +
            model.County +
            ', ' +
            model.City +
            ', ' +
            model.State +
            ', ' +
            model.Zip;
        } else {
          UnFormattedAddress =
            model.AddressLine1 + ', ' + model.County + ', ' + model.City + ', ' + model.State + ', ' + model.Zip;
        }
      }
      const updatedLocation = {
        ...currentLocations[editLocationIndex],
        AddressLine1: model.AddressLine1 || '',
        AddressLine2: model.AddressLine2 || '',
        City: model.City || '',
        State: model.State || '',
        County: model.County || '',
        Zip: model.Zip || '',
        Country: model.Country || 'United States',
        IsManual: model.IsManual || true,
        IsPrimaryPracticeLocation: false,
        UnFormattedAddress: UnFormattedAddress,
        id: currentLocations[editLocationIndex].id,
      };
      const updatedLocations = currentLocations.map((l, idx) => (idx === editLocationIndex ? updatedLocation : l));
      setState({
        additionalLocations: updatedLocations,
        editLocationIndex: null,
        locationFormData: {
          Address: {},
          NickName: '',
          LocationNumber: 0,
          IsValid: false,
          IsPrimaryPracticeLocation: false,
          IsManual: false,
          StreetName: '',
          AddressLine1: '',
          AddressLine2: '',
          City: '',
          State: '',
          County: '',
          CountyCode: '',
          Zip: '',
          Country: '',
          CountryCode: '',
          PlaceId: '',
          Number: 0,
          Name: '',
          Long: '',
          Lat: '',
          Description: '',
          AddressType: 'p',
          FormattedAddress: '',
          UnFormattedAddress: '',
          Status: '',
          AptSuite: '',
          PoBox: '',
          CityCode: '',
          Territory: '',
          TerritoryCode: '',
        },
        locationUpdateTrigger: (prev) => prev + 1,
      });

      // Update Redux store
      if (policyData) {
        setAdditionalLocations(updatedLocations, policyData);
      }
    } else {
      // Add new location
      const currentLocations = getAdditionalLocations(policyData);
      if (
        policyData?.Risks?.PayrollProtectionUnit?.[0]?.PractitionerRiskAttributes?.PracticeLocationAddresses.length > 4
      ) {
        toast.error('You can add a maximum of 5 addresses.');
        return;
      }
      const newLocation = {
        NickName: '',
        LocationNumber: 0,
        IsValid: false,
        IsPrimaryPracticeLocation: false,
        IsManual: model.Address.IsManual || false,
        StreetName: '',
        AddressLine1: model.Address.AddressLine1 || '',
        AddressLine2: model.Address.AddressLine1 || '',
        City: model.Address.City || '',
        State: model.Address.State || '',
        County: model.Address.County || '',
        Zip: model.Address.Postalcode || model.Address.Zip || '',
        Country: model.Address.Country || 'United States',
        CountyCode: '',
        CountryCode: '',
        PlaceId: model.Address.PlaceId,
        Number: 0,
        Name: '',
        Long: model.Address.Long,
        Lat: model.Address.Lat,
        Description: '',
        AddressType: 'p',
        FormattedAddress: model.Address.FormattedAddress,
        UnFormattedAddress: model.Address.UnFormattedAddress,
        Status: '',
        AptSuite: '',
        PoBox: '',
        CityCode: '',
        Territory: '',
        TerritoryCode: '',
      };
      // const newLocation = {
      //   id: Date.now() + Math.random(),
      //   AddressLine1: model.AddressLine1 || '',
      //   AddressLine2: model.AddressLine2 || '',
      //   City: model.City || '',
      //   State: model.State || '',
      //   County: model.County || '',
      //   Zip: model.Zip || '',
      //   Country: model.Country || 'United States',
      //   IsManual: model.IsManual || false,
      //   IsPrimaryPracticeLocation: false,
      // };
      const updatedLocations = [...currentLocations, newLocation];
      setState({
        additionalLocations: updatedLocations,
        editLocationIndex: null,
        locationFormData: {
          Address: {},
          NickName: '',
          LocationNumber: 0,
          IsValid: false,
          IsPrimaryPracticeLocation: false,
          IsManual: false,
          StreetName: '',
          AddressLine1: '',
          AddressLine2: '',
          City: '',
          State: '',
          County: '',
          CountyCode: '',
          Zip: '',
          Country: '',
          CountryCode: '',
          PlaceId: '',
          Number: 0,
          Name: '',
          Long: '',
          Lat: '',
          Description: '',
          AddressType: 'p',
          FormattedAddress: '',
          UnFormattedAddress: '',
          Status: '',
          AptSuite: '',
          PoBox: '',
          CityCode: '',
          Territory: '',
          TerritoryCode: '',
        },
        locationUpdateTrigger: (prev) => prev + 1,
      });

      // Update Redux store
      if (policyData) {
        setAdditionalLocations(updatedLocations, policyData);
      }
    }
  };

export const onApplicationPolicyHolderAdditionalLocationEdit = ({ payload: { state, setState } }) => {
  const { row, rowIndex } = state;
  setState({
    editLocationIndex: rowIndex,
    locationFormData: {
      AddressLine1: row.AddressLine1 || '',
      AddressLine2: row.AddressLine2 || '',
      City: row.City || '',
      State: row.State || '',
      County: row.County || '',
      Zip: row.Zip || '',
      Country: row.Country || 'United States',
      IsManual: row.IsManual || false,
    },
  });
};

export const onApplicationPolicyHolderAdditionalLocationDelete = ({ payload: { state, setState, policyData } }) => {
  const { row } = state;
  const currentLocations = getAdditionalLocations();
  const updatedLocations = currentLocations.filter((loc) => loc.id !== row.id);
  setState({
    additionalLocations: updatedLocations,
    editLocationIndex: null,
    locationFormData: {
      AddressLine1: '',
      AddressLine2: '',
      City: '',
      State: '',
      County: '',
      Zip: '',
      Country: 'United States',
      IsManual: false,
    },
  });
  if (policyData) {
    policyData.Risks.PayrollProtectionUnit[0].PractitionerRiskAttributes.PracticeLocationAddresses[0].OfficeContacts =
      updatedLocations;
    setOfficeContacts(updatedLocations, policyData);
  }
};

export const onApplicationPolicyHolderAdditionalLocationReset =
  ({ payload: { _state, setState, policyData } }) =>
  (_dispatch, _getState) => {
    setState({
      editLocationIndex: null,
      locationFormData: {
        AddressLine1: '',
        AddressLine2: '',
        City: '',
        State: '',
        County: '',
        Zip: '',
        Country: 'United States',
        IsManual: false,
      },
    });
    if (policyData) {
      setStoreState(storeKeys.PolicyReducer, policyData);
    }
    _dispatch({ type: PUT_POLICY, payload: policyData });
  };

export const onApplicationPolicyHolderAdditionalContactReset =
  ({ payload: { _state, setState, policyData } }) =>
  (_dispatch, _getState) => {
    setState({
      editIndex: null,
      contactFormData: {
        ContactType: '',
        ContactName: '',
        ContactNumber: '',
        ContactEmail: '',
      },
    });
    if (policyData) {
      // setOfficeContacts([], policyData);
      setStoreState(storeKeys.PolicyReducer, policyData);
    }
    _dispatch({ type: PUT_POLICY, payload: policyData });
  };

export const onApplicationPolicyHolderOnFormSubmit =
  ({ payload, saveAndExit }) =>
  async (dispatch, _getState) => {
    const model = structuredClone(payload);
    applyResolvedPayrollProtectionAgesToPolicy(model);
    const IdentifierType = model.Risks.PayrollProtectionUnit[0].PractitionerRiskAttributes.IdentifierType;

    if (
      IdentifierType === 'NPI' &&
      model.Risks.PayrollProtectionUnit[0].PractitionerRiskAttributes.NPINumber.length !== 10
    ) {
      toast.error('Please enter 10 digit National Provider Identifier (NPI) number');
      return null;
    }

    NProgress.start();
    let resp;
    //Delete AddAdditionalPractitioner from model
    delete model.AddAdditionalPractitioner;

    const ApplicationReducer = getStoreState(storeKeys.ApplicationReducer);
    if (saveAndExit) {
      setStoreState(storeKeys.ApplicationReducer, {
        ...ApplicationReducer,
        IsSaveAndExit: true,
      });
    } else {
      setStoreState(storeKeys.ApplicationReducer, {
        ...ApplicationReducer,
        IsSaveAndExit: false,
      });
    }

    try {
      resp = await save(model, commonKeys.EmptyString, routeKeys.PolicyHolderSubmit, commonKeys.EmptyString, 0);
      if (resp) {
        dispatch({ type: PUT_POLICY, payload: resp });
      }
    } catch (error) {
      if (!isPayrollProtectionAgeQualificationError(error?.message)) {
        toast.error(error.message);
      }
      setStoreState(storeKeys.ApplicationReducer, {
        ...ApplicationReducer,
        IsSaveAndExit: false,
      });
      NProgress.done();
      throw new Error(error);
    }
    NProgress.done();
    return resp;
  };

export const onDeclineQualificationModalSubmit =
  ({ payload, saveAndExit }) =>
  async (dispatch, _getState) => {
    NProgress.start();
    try {
      const model = structuredClone(payload);
      let resp = await save(
        model,
        commonKeys.EmptyString,
        routeKeys.AgeQualificationDeclineSubmit,
        commonKeys.EmptyString,
        0
      );
      if (resp) {
        dispatch({ type: PUT_POLICY, payload: resp });
      }
      return resp;
    } catch (error) {
      toast.error(error.message);
      setStoreState(storeKeys.ApplicationReducer, {
        ...ApplicationReducer,
        IsSaveAndExit: false,
      });
      throw new Error(error);
    } finally {
      NProgress.done();
    }
  };
